import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Truck, Trash, PencilSimple, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", address: "", contact_person: "", phone: "", email: "", bank_name: "", account_name: "", account_number: "", iban: "", swift: "" };
const FIELDS = [
  ["name", "Supplier Name"], ["contact_person", "Contact Person"], ["phone", "Phone"], ["email", "Email"],
  ["bank_name", "Bank Name"], ["account_name", "Account Name"], ["account_number", "Account Number"],
  ["iban", "IBAN"], ["swift", "SWIFT / BIC"],
];

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditingId(s.id); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name is required"); return; }
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] || ""]));
      if (editingId) { await api.put(`/suppliers/${editingId}`, payload); toast.success("Supplier updated"); }
      else { await api.post("/suppliers", payload); toast.success("Supplier created"); }
      setOpen(false); setForm(EMPTY); setEditingId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this supplier? This cannot be undone.")) return;
    try { await api.delete(`/suppliers/${id}`); toast.success("Supplier deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between fade-up">
        <div>
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-2">Vendors</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Suppliers</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button className="rounded-sm gap-2" data-testid="add-supplier-btn" onClick={openCreate}><Plus size={16} /> New Supplier</Button>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{editingId ? "Edit Supplier" : "New Supplier"}</DialogTitle>
              <DialogDescription className="sr-only">Enter supplier contact and bank details used for lease payments.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              {FIELDS.map(([k, label]) => (
                <div key={k} className={k === "name" ? "col-span-2" : ""}>
                  <Label>{label}</Label>
                  <Input value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-1.5 rounded-sm" data-testid={`supplier-${k}`} />
                </div>
              ))}
              <div className="col-span-2">
                <Label>Address</Label>
                <Textarea value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 rounded-sm" data-testid="supplier-address" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="rounded-sm" data-testid="save-supplier-btn">{editingId ? "Save Changes" : "Create Supplier"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="rounded-sm border-dashed border-border shadow-none p-16 text-center bg-card fade-up">
          <Truck size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm">No suppliers yet. Add lessors/vendors with their bank details for payments.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s, i) => (
            <Card key={s.id} className="rounded-sm border-border shadow-none p-6 bg-card fade-up hover:-translate-y-1 transition-transform group"
              style={{ animationDelay: `${i * 40}ms` }} data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-display text-lg font-semibold tracking-tight">{s.name}</h3>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`} className="h-7 w-7 text-muted-foreground hover:text-primary rounded-sm"><PencilSimple size={15} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(s.id)} data-testid={`delete-supplier-${s.id}`} className="h-7 w-7 text-muted-foreground hover:text-destructive rounded-sm"><Trash size={15} /></Button>
                </div>
              </div>
              {(s.contact_person || s.phone || s.email) && (
                <p className="text-xs text-muted-foreground mb-1">{[s.contact_person, s.phone, s.email].filter(Boolean).join(" · ")}</p>
              )}
              {s.address && <p className="text-xs text-muted-foreground mb-3">{s.address}</p>}
              {(s.bank_name || s.account_number || s.iban) && (
                <div className="border-t border-border pt-3 mt-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-[10px] font-medium tracking-[0.15em] uppercase text-muted-foreground mb-1"><Bank size={13} /> Bank Details</div>
                  {s.bank_name && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Bank</span><span className="font-mono">{s.bank_name}</span></div>}
                  {s.account_name && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Account</span><span className="font-mono">{s.account_name}</span></div>}
                  {s.account_number && <div className="flex justify-between text-xs"><span className="text-muted-foreground">A/C No.</span><span className="font-mono">{s.account_number}</span></div>}
                  {s.iban && <div className="flex justify-between text-xs"><span className="text-muted-foreground">IBAN</span><span className="font-mono">{s.iban}</span></div>}
                  {s.swift && <div className="flex justify-between text-xs"><span className="text-muted-foreground">SWIFT</span><span className="font-mono">{s.swift}</span></div>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
